import React, { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { Plus, Edit2, Trash2, Eye, Mail, Save, X, Check, AlertCircle } from 'lucide-react';
import { Button } from '../components/ui/Button';
import { Card } from '../components/ui/Card';

interface EmailTemplate {
  id: string;
  name: string;
  type: string;
  subject: string;
  html_content: string;
  variables: string[];
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

interface QueueStats {
  pending: number;
  sent: number;
  failed: number;
}

export default function EmailTemplates() {
  const [templates, setTemplates] = useState<EmailTemplate[]>([]);
  const [queueStats, setQueueStats] = useState<QueueStats>({ pending: 0, sent: 0, failed: 0 });
  const [loading, setLoading] = useState(true);
  const [selectedTemplate, setSelectedTemplate] = useState<EmailTemplate | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const [formData, setFormData] = useState({
    name: '',
    type: 'production',
    subject: '',
    html_content: '',
    variables: [] as string[],
    is_active: true,
  });

  useEffect(() => {
    loadTemplates();
    loadQueueStats();
  }, []);

  const loadTemplates = async () => {
    try {
      const { data, error } = await supabase
        .from('email_templates')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      setTemplates(data || []);
    } catch (err) {
      console.error('Error loading templates:', err);
      setError('Error al cargar plantillas');
    } finally {
      setLoading(false);
    }
  };

  const loadQueueStats = async () => {
    try {
      const { count: pendingCount } = await supabase
        .from('email_notification_queue')
        .select('*', { count: 'exact', head: true })
        .eq('status', 'pending');

      const { count: sentCount } = await supabase
        .from('email_notification_queue')
        .select('*', { count: 'exact', head: true })
        .eq('status', 'sent');

      const { count: failedCount } = await supabase
        .from('email_notification_queue')
        .select('*', { count: 'exact', head: true })
        .eq('status', 'failed');

      setQueueStats({
        pending: pendingCount || 0,
        sent: sentCount || 0,
        failed: failedCount || 0,
      });
    } catch (err) {
      console.error('Error loading queue stats:', err);
    }
  };

  const handleCreate = () => {
    setIsCreating(true);
    setIsEditing(true);
    setFormData({
      name: '',
      type: 'production',
      subject: '',
      html_content: '',
      variables: [],
      is_active: true,
    });
    setSelectedTemplate(null);
  };

  const handleEdit = (template: EmailTemplate) => {
    setIsCreating(false);
    setIsEditing(true);
    setSelectedTemplate(template);
    setFormData({
      name: template.name,
      type: template.type,
      subject: template.subject,
      html_content: template.html_content,
      variables: template.variables,
      is_active: template.is_active,
    });
  };

  const handleCancel = () => {
    setIsEditing(false);
    setIsCreating(false);
    setSelectedTemplate(null);
    setShowPreview(false);
    setError(null);
  };

  const handleSave = async () => {
    try {
      setError(null);

      if (!formData.name || !formData.subject || !formData.html_content) {
        setError('Por favor completa todos los campos requeridos');
        return;
      }

      if (isCreating) {
        const { error } = await supabase
          .from('email_templates')
          .insert({
            name: formData.name,
            type: formData.type,
            subject: formData.subject,
            html_content: formData.html_content,
            variables: formData.variables,
            is_active: formData.is_active,
          });

        if (error) throw error;
        setSuccess('Plantilla creada exitosamente');
      } else if (selectedTemplate) {
        const { error } = await supabase
          .from('email_templates')
          .update({
            type: formData.type,
            subject: formData.subject,
            html_content: formData.html_content,
            variables: formData.variables,
            is_active: formData.is_active,
            updated_at: new Date().toISOString(),
          })
          .eq('id', selectedTemplate.id);

        if (error) throw error;
        setSuccess('Plantilla actualizada exitosamente');
      }

      await loadTemplates();
      handleCancel();
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      console.error('Error saving template:', err);
      setError('Error al guardar la plantilla');
    }
  };

  const handleDelete = async (id: string) => {
    if (!confirm('¿Estás seguro de eliminar esta plantilla?')) return;

    try {
      const { error } = await supabase
        .from('email_templates')
        .delete()
        .eq('id', id);

      if (error) throw error;
      setSuccess('Plantilla eliminada exitosamente');
      await loadTemplates();
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      console.error('Error deleting template:', err);
      setError('Error al eliminar la plantilla');
    }
  };

  const handleToggleActive = async (template: EmailTemplate) => {
    try {
      const { error } = await supabase
        .from('email_templates')
        .update({ is_active: !template.is_active, updated_at: new Date().toISOString() })
        .eq('id', template.id);

      if (error) throw error;
      await loadTemplates();
    } catch (err) {
      console.error('Error toggling template:', err);
      setError('Error al cambiar estado de la plantilla');
    }
  };

  const processQueue = async () => {
    try {
      setProcessing(true);
      setError(null);

      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/send-email-notification/process-queue`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
            'Content-Type': 'application/json',
          },
        }
      );

      if (!response.ok) {
        throw new Error('Error al procesar la cola de emails');
      }

      const result = await response.json();
      setSuccess(`Procesados: ${result.processed || 0} emails`);
      await loadQueueStats();
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      console.error('Error processing queue:', err);
      setError('Error al procesar la cola de emails');
    } finally {
      setProcessing(false);
    }
  };

  const getTypeLabel = (type: string) => {
    const types: Record<string, string> = {
      production: 'Producción',
      sample: 'Muestras',
      express: 'Express',
      express_sample: 'Muestras Express',
      system: 'Sistema',
      crm_reminder: 'Recordatorio CRM',
    };
    return types[type] || type;
  };

  const getTypeColor = (type: string) => {
    const colors: Record<string, string> = {
      production: 'bg-blue-100 text-blue-800',
      sample: 'bg-pink-100 text-pink-800',
      express: 'bg-orange-100 text-orange-800',
      express_sample: 'bg-yellow-100 text-yellow-800',
      system: 'bg-gray-100 text-gray-800',
      crm_reminder: 'bg-purple-100 text-purple-800',
    };
    return colors[type] || 'bg-gray-100 text-gray-800';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Plantillas de Email</h1>
          <p className="text-gray-600 mt-1">Gestiona las plantillas HTML para notificaciones por email</p>
        </div>
        <Button onClick={handleCreate}>
          <Plus className="h-4 w-4 mr-2" />
          Nueva Plantilla
        </Button>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-lg flex items-center gap-2">
          <AlertCircle className="h-5 w-5" />
          {error}
        </div>
      )}

      {success && (
        <div className="bg-green-50 border border-green-200 text-green-800 px-4 py-3 rounded-lg flex items-center gap-2">
          <Check className="h-5 w-5" />
          {success}
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Pendientes</p>
              <p className="text-3xl font-bold text-orange-600">{queueStats.pending}</p>
            </div>
            <Mail className="h-12 w-12 text-orange-200" />
          </div>
        </Card>
        <Card className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Enviados</p>
              <p className="text-3xl font-bold text-green-600">{queueStats.sent}</p>
            </div>
            <Check className="h-12 w-12 text-green-200" />
          </div>
        </Card>
        <Card className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Fallidos</p>
              <p className="text-3xl font-bold text-red-600">{queueStats.failed}</p>
            </div>
            <AlertCircle className="h-12 w-12 text-red-200" />
          </div>
        </Card>
      </div>

      {isEditing ? (
        <Card className="p-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-semibold">
              {isCreating ? 'Nueva Plantilla' : 'Editar Plantilla'}
            </h2>
            <div className="flex gap-2">
              <Button variant="outline" onClick={handleCancel}>
                <X className="h-4 w-4 mr-2" />
                Cancelar
              </Button>
              <Button onClick={() => setShowPreview(!showPreview)} variant="outline">
                <Eye className="h-4 w-4 mr-2" />
                {showPreview ? 'Editor' : 'Vista Previa'}
              </Button>
              <Button onClick={handleSave}>
                <Save className="h-4 w-4 mr-2" />
                Guardar
              </Button>
            </div>
          </div>

          {showPreview ? (
            <div className="border rounded-lg p-4 bg-white">
              <div className="mb-4 pb-4 border-b">
                <p className="text-sm text-gray-600">Asunto:</p>
                <p className="font-medium">{formData.subject}</p>
              </div>
              <div
                className="prose max-w-none"
                dangerouslySetInnerHTML={{ __html: formData.html_content }}
              />
            </div>
          ) : (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Nombre identificador
                  </label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    disabled={!isCreating}
                    className="w-full px-3 py-2 border rounded-lg disabled:bg-gray-100"
                    placeholder="production_created"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Tipo
                  </label>
                  <select
                    value={formData.type}
                    onChange={(e) => setFormData({ ...formData, type: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg"
                  >
                    <option value="production">Producción</option>
                    <option value="sample">Muestras</option>
                    <option value="express">Express</option>
                    <option value="express_sample">Muestras Express</option>
                    <option value="system">Sistema</option>
                    <option value="crm_reminder">Recordatorio CRM</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Asunto del email
                </label>
                <input
                  type="text"
                  value={formData.subject}
                  onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg"
                  placeholder="Nueva Producción: {{production_name}}"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Usa variables como: {'{{'} user_name {'}}'}
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Contenido HTML
                </label>
                <textarea
                  value={formData.html_content}
                  onChange={(e) => setFormData({ ...formData, html_content: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg font-mono text-sm"
                  rows={20}
                  placeholder="<html>...</html>"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Variables disponibles: user_name, title, message, action_url, created_date, production_name, sample_name, express_name, client_name, reminder_title, reminder_description, reminder_date
                </p>
              </div>

              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="is_active"
                  checked={formData.is_active}
                  onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                  className="rounded"
                />
                <label htmlFor="is_active" className="text-sm font-medium text-gray-700">
                  Plantilla activa
                </label>
              </div>
            </div>
          )}
        </Card>
      ) : (
        <div className="grid gap-4">
          {templates.map((template) => (
            <Card key={template.id} className="p-6">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <h3 className="text-lg font-semibold">{template.name}</h3>
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${getTypeColor(template.type)}`}>
                      {getTypeLabel(template.type)}
                    </span>
                    {template.is_active ? (
                      <span className="px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                        Activa
                      </span>
                    ) : (
                      <span className="px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                        Inactiva
                      </span>
                    )}
                  </div>
                  <p className="text-sm text-gray-600 mb-2">
                    <strong>Asunto:</strong> {template.subject}
                  </p>
                  <p className="text-xs text-gray-500">
                    Última actualización: {new Date(template.updated_at).toLocaleDateString('es-AR')}
                  </p>
                </div>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleToggleActive(template)}
                  >
                    {template.is_active ? 'Desactivar' : 'Activar'}
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleEdit(template)}
                  >
                    <Edit2 className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleDelete(template.id)}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
